import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, ShieldCheck } from 'lucide-react';
import { financeApi } from '../../api/finance';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { PAYABLE_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission, useAuth } from '../../auth/AuthContext';

const STATUS_LABELS: Record<string, string> = { outstanding: 'Outstanding', partially_paid: 'Partially Paid', paid: 'Paid' };

export function PayableDetailPage() {
  const { id } = useParams<{ id: string }>();
  const payableId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('finance.payables.pay');
  const { user } = useAuth();
  const [paying, setPaying] = useState(false);
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [fiscalYearId, setFiscalYearId] = useState<number | null>(null);
  const [periodId, setPeriodId] = useState<number | null>(null);

  const payableQuery = useQuery({ queryKey: ['accounts_payable', payableId], queryFn: () => financeApi.getPayable(payableId) });
  const docsQuery = useQuery({ queryKey: ['payable-documents', payableId], queryFn: () => genericDocumentApi.listFor('accounts_payable', payableId) });
  const fiscalYearsQuery = useQuery({ queryKey: ['finance-fiscal-years'], queryFn: () => financeApi.listFiscalYears(), enabled: paying });
  const fiscalYearDetailQuery = useQuery({ queryKey: ['finance-fiscal-year-detail', fiscalYearId], queryFn: () => financeApi.getFiscalYear(fiscalYearId!), enabled: !!fiscalYearId });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['accounts_payable', payableId] });

  const payMutation = useMutation({
    mutationFn: () => financeApi.payPayable(payableId, { amount: Number(amount), paymentDate, fiscalYearId: fiscalYearId!, accountingPeriodId: periodId! }),
    onSuccess: (result) => { showSuccess(`Payment recorded - journal ${result.data.journalReference}, now ${result.data.status}.`); invalidate(); setPaying(false); setAmount(''); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record payment - check Finance Settings has the Bank account configured.'),
  });

  const authorizeMutation = useMutation({
    mutationFn: () => financeApi.authorizePayable(payableId),
    onSuccess: () => { showSuccess('Sent for payment authorization.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to request authorization.'),
  });

  const decideAuthMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => financeApi.decidePayableAuthorization(payableId, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  const periods = fiscalYearDetailQuery.data?.periods ?? [];

  if (!payableQuery.data) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;
  const { data: p, payments, authorization } = payableQuery.data;

  // Real authorization gate state (Section: Payable payment-authorization
  // gate) - never guessed, straight from the approvals the backend
  // returns. No entries at all = never requested; any 'rejected' =
  // permanently blocked; all 'approved' with entries = cleared to pay.
  const noAuthorizationYet = authorization.length === 0;
  const myPendingAuth = authorization.find((a) => a.approverUserId === user?.id && a.decision === 'pending');
  const authRejected = authorization.some((a) => a.decision === 'rejected');
  const authApproved = authorization.length > 0 && authorization.every((a) => a.decision === 'approved');

  return (
    <div className="page">
      <Link to="/finance/payables">&larr; Back to Payables</Link>
      <RecordHeader
        title={`Payable - ${p.payable_reference}`}
        entityType="accounts_payable"
        entityId={payableId}
        fields={[
          { label: 'Source', value: p.source_module },
          { label: 'Amount', value: Number(p.amount).toLocaleString() },
          { label: 'Paid', value: Number(p.amount_paid).toLocaleString() },
          { label: 'Outstanding', value: (Number(p.amount) - Number(p.amount_paid)).toLocaleString() },
          { label: 'Status', value: <StatusBadge status={p.status} label={STATUS_LABELS[p.status]} /> },
        ]}
        actions={
          <span className="icon-action-group">
            {p.status !== 'paid' && noAuthorizationYet && (
              <button className="btn-primary" onClick={() => authorizeMutation.mutate()} disabled={authorizeMutation.isPending}><ShieldCheck size={16} className="inline-icon" /> Request Payment Authorization</button>
            )}
            {myPendingAuth && (
              <>
                <button className="btn-primary" onClick={() => decideAuthMutation.mutate('approved')} disabled={decideAuthMutation.isPending}>Approve Authorization</button>
                <button className="button-danger" onClick={() => decideAuthMutation.mutate('rejected')} disabled={decideAuthMutation.isPending}>Reject Authorization</button>
              </>
            )}
            {p.status !== 'paid' && authApproved && (
              <button className="btn-primary" onClick={() => setPaying((v) => !v)}><Banknote size={16} className="inline-icon" /> Record Payment</button>
            )}
          </span>
        }
        readOnly={!canManage}
      />
      {authRejected && <p className="error-text">Payment authorization was rejected for this payable - it cannot be paid.</p>}

      {paying && (
        <section className="card">
          <h2>Record Payment</h2>
          <label>Amount<input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
          <label>Payment Date<input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} /></label>
          <label>
            Fiscal Year
            <select onChange={(e) => setFiscalYearId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {fiscalYearsQuery.data?.data.map((fy) => <option key={fy.id} value={fy.id}>{fy.year_label}</option>)}
            </select>
          </label>
          <label>
            Accounting Period
            <select onChange={(e) => setPeriodId(Number(e.target.value) || null)}>
              <option value="">- select -</option>
              {periods.map((per) => <option key={per.id} value={per.id}>{per.period_label}</option>)}
            </select>
          </label>
          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={() => payMutation.mutate()} disabled={payMutation.isPending || !amount || !periodId}>{payMutation.isPending ? 'Recording…' : 'Record Payment'}</button>
            <button type="button" className="btn-secondary" onClick={() => setPaying(false)}>Cancel</button>
          </div>
        </section>
      )}

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  {payments && payments.length > 0 && (
                    <>
                      <h3>Payment History</h3>
                      <DataTable
                        rows={payments}
                        rowKey={(pmt) => pmt.id}
                        emptyMessage="No payments recorded yet."
                        columns={[
                          { header: 'Date', render: (pmt) => pmt.payment_date },
                          { header: 'Amount', render: (pmt) => Number(pmt.amount).toLocaleString() },
                          { header: 'Journal', render: (pmt) => pmt.journal_reference ?? '-' },
                        ]}
                      />
                    </>
                  )}
                </section>
                <CommentsThread entityType="accounts_payable" entityId={payableId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('accounts_payable', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('accounts_payable', payableId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('accounts_payable', documentId)}
                queryKeyToInvalidate={['payable-documents', payableId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="accounts_payable" entityId={payableId} actionStageMap={PAYABLE_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
